/**
 * Развёртка одной стены — как лист рабочего чертежа.
 *
 * Что рисуется и почему именно так:
 *   • сама стена — чистой белой поверхностью, без штриховки и без цвета отделки:
 *     развёртка — чертёж для разметки и монтажа, а не картинка интерьера;
 *   • предметы у стены — своим рисунком спереди или сбоку (смотря как предмет повёрнут
 *     к наблюдателю), залитым белым: предмет закрывает стену за собой, как в жизни.
 *     Предмет, которому рисунок для развёртки ещё не нарисован, — контур с подписью.
 *     Розетки и размеры рисуются поверх предметов: они нужны на чертеже и за мебелью;
 *   • перекрытия сверху и снизу и примыкающие стены по краям — наоборот,
 *     в разрезе, со штриховкой по материалу: это то, что «перерезано»;
 *   • размеры двумя цепочками снизу — проёмы и точки — и высотами по бокам,
 *     чтобы по чертежу можно было размечать стену рулеткой.
 */
import type { PointerEvent } from "react";
import { DimensionLine } from "@/widgets/plan";
import { getCatalogItem, isWaterPoint, windowItemOf } from "@/shared/catalog";
import {
	ElectricalItemShape,
	PlumbingItemShape,
	WaterPointShape,
	CatalogSymbol,
	hasElevationSymbol,
	planInk,
} from "@/widgets/catalog";
import type { Selection } from "@/widgets/editor";
import { SelectionKind, useLengthUnit } from "@/shared/store";
import { formatMillimeters } from "@/shared/lib/geometry";
import { sheetPoint, type SheetLayout } from "../layout";
import { CatalogSectionKind, itemDimensions, type Fixture, type FloorItem } from "@/shared/plan";

interface Props {
	sheet: SheetLayout;
	mmPerPx: number;
	selection: Selection;
	interactive: boolean;
	showDimensions: boolean;
	/** В PDF белые фоны соседних стен не должны закрывать предметы на стыке. */
	fillWallFace?: boolean;
	onFixturePointerDown: (e: PointerEvent<SVGGElement>, fixtureId: string) => void;
	onFloorItemPointerDown: (
		e: PointerEvent<SVGGElement>,
		item: FloorItem,
		elevation: number,
		height: number,
	) => void;
	onWallPointerDown: (e: PointerEvent<SVGRectElement>) => void;
}

const FACE = "#ffffff";
const LINE = "#2b3440";
/** Толщина внешнего контура внутренней ленты стен, мм. */
const ROOM_OUTLINE_STROKE_MM = 2.0;
/** Толщина линий между соседними стенами комнаты, мм. */
const ROOM_INTERNAL_DIVIDER_STROKE_MM = 2.0;

/** Минимальный размер значка точки на экране, пикселей. */
const FIXTURE_MIN_PX = 18;

/** Кегль подписи предмета, пикселей. */
const LABEL_FONT_PX = 11;

/**
 * Подпись предмета, которая влезает в его фасад: целиком, первым словом («Тумба»,
 * «Монстера») или никак — подпись не должна вылезать за предмет и наезжать на соседей.
 */
const fitLabel = (name: string, width: number, height: number, mmPerPx: number): string | null => {
	const charWidth = LABEL_FONT_PX * 0.56 * mmPerPx;
	const room = width - 8 * mmPerPx;
	if (height < LABEL_FONT_PX * 1.4 * mmPerPx) return null;
	if (name.length * charWidth <= room) return name;
	const word = name.split(/[\s,]+/)[0] ?? "";
	return word && word.length * charWidth <= room ? word : null;
};

export const ElevationSheet = ({
	sheet,
	mmPerPx,
	selection,
	interactive,
	showDimensions,
	fillWallFace = true,
	onFixturePointerDown,
	onFloorItemPointerDown,
	onWallPointerDown,
}: Props) => {
	const unit = useLengthUnit();
	const { elevation } = sheet;

	const px = (n: number) => n * mmPerPx;
	const at = (x: number, y: number) => sheetPoint(sheet, x, y);
	const top = at(0, elevation.height);
	const floor = at(0, 0);
	const { width, height } = elevation;
	const isFirstWall = elevation.index === 1;

	return (
		<g>
			{/* Внутренние грани комнаты: стены стоят вплотную, стыки и контур обозначены тонкой линией. */}
			<rect
				data-role="wall-face"
				x={sheet.x}
				y={top.y}
				width={width}
				height={height}
				fill={fillWallFace ? FACE : "none"}
				stroke="none"
				style={{ cursor: "default" }}
				onPointerDown={onWallPointerDown}
			/>
			<line
				x1={sheet.x}
				y1={top.y}
				x2={sheet.x + width}
				y2={top.y}
				stroke={LINE}
				strokeWidth={px(ROOM_OUTLINE_STROKE_MM)}
			/>
			<line
				x1={sheet.x}
				y1={floor.y}
				x2={sheet.x + width}
				y2={floor.y}
				stroke={LINE}
				strokeWidth={px(ROOM_OUTLINE_STROKE_MM)}
			/>
			{isFirstWall && (
				<line
					x1={sheet.x}
					y1={top.y}
					x2={sheet.x}
					y2={floor.y}
					stroke={LINE}
					strokeWidth={px(ROOM_OUTLINE_STROKE_MM)}
				/>
			)}
			<line
				x1={sheet.x + width}
				y1={top.y}
				x2={sheet.x + width}
				y2={floor.y}
				stroke={LINE}
				strokeWidth={px(ROOM_INTERNAL_DIVIDER_STROKE_MM)}
			/>

			{/* Проёмы */}
			{elevation.openings.map(({ opening, x0, x1, y0, y1 }) => {
				const box = at(x0, y1);
				const w = x1 - x0;
				const h = y1 - y0;
				const isWindow = opening.kind === "window";
				// Импосты между створками — сколько створок, знает тип окна
				const sashes = isWindow ? windowItemOf(opening.item).sashes : 1;
				return (
					<g key={opening.id} style={{ pointerEvents: "none" }}>
						<rect
							x={box.x}
							y={box.y}
							width={w}
							height={h}
							fill={isWindow ? "#eaf3fb" : "#f7f8fa"}
							stroke={LINE}
							strokeWidth={px(1.1)}
						/>
						{/* Коробка внутри проёма */}
						<rect
							x={box.x + 60}
							y={box.y + 60}
							width={Math.max(0, w - 120)}
							height={Math.max(0, h - (isWindow ? 120 : 60))}
							fill="none"
							stroke={LINE}
							strokeWidth={px(0.8)}
						/>
						{Array.from({ length: sashes - 1 }, (_, i) => box.x + (w * (i + 1)) / sashes).map(
							(x) => (
								<line
									key={x}
									x1={x}
									y1={box.y + 60}
									x2={x}
									y2={box.y + h - 60}
									stroke={LINE}
									strokeWidth={px(0.8)}
								/>
							),
						)}
					</g>
				);
			})}

			{/* Предметы не обрезаются краем стены: в PDF должен быть виден весь габарит. */}
			<g>
				{elevation.floorItems.map(
					({ item, x, y, width: itemWidth, height: itemHeight, view, mirror }) => {
						const catalogItem = getCatalogItem(item.ref);
						const box = at(x - itemWidth / 2, y + itemHeight);
						// Если отдельного бокового вида нет, печатаем фасад вместо белой заглушки.
						const front = hasElevationSymbol(catalogItem, "front");
						const side = hasElevationSymbol(catalogItem, "side");
						const drawn = front || side;
						const facing = view === "side" && side ? "side" : "front";
						// Видимая на стене часть предмета — подпись центруем и ужимаем по ней
						const left = Math.max(box.x, sheet.x);
						const right = Math.min(box.x + itemWidth, sheet.x + width);
						const label = front
							? null
							: fitLabel(catalogItem?.name ?? "Предмет", right - left, itemHeight, mmPerPx);
						// Светлый предмет (белая ванна) затемняем, иначе контур не виден
						const ink = planInk(catalogItem?.color ?? "#9aa3b2");
						return (
							<g
								key={item.id}
								data-elevation-floor-item
								style={{ cursor: interactive ? "grab" : "default" }}
								onPointerDown={(e) => onFloorItemPointerDown(e, item, y, itemHeight)}
							>
								{drawn ? (
									<>
										{/* Зона захвата во весь габарит: между ножками тоже можно взять */}
										<rect
											x={box.x}
											y={box.y}
											width={itemWidth}
											height={itemHeight}
											fill="transparent"
										/>
										<g
											transform={`translate(${box.x + itemWidth / 2} ${box.y + itemHeight / 2})${
												mirror ? " scale(-1 1)" : ""
											}`}
										>
											<CatalogSymbol
												item={catalogItem}
												width={itemWidth}
												height={itemHeight}
												color={ink}
												mmPerPx={mmPerPx}
												facing={facing}
											/>
										</g>
									</>
								) : (
									<rect
										x={box.x}
										y={box.y}
										width={itemWidth}
										height={itemHeight}
										// Коробка сбоку закрывает стену, как и рисунок; не нарисованный предмет —
										// контур без заливки, прозрачная заливка оставляет зону захвата
										fill={front ? "#ffffff" : "transparent"}
										stroke={ink}
										strokeWidth={front ? 12 : px(0.8)}
									/>
								)}
								{label && (
									<text
										x={(left + right) / 2}
										// У верха фасада, а не в центре: по центру предмета часто идёт
										// размерная линия (высота подоконника над кроватью)
										y={box.y + Math.min(itemHeight / 2, px(LABEL_FONT_PX * 1.1))}
										textAnchor="middle"
										dominantBaseline="middle"
										fontSize={px(LABEL_FONT_PX)}
										fill={ink}
										style={{ pointerEvents: "none" }}
									>
										{label}
									</text>
								)}
							</g>
						);
					},
				)}
			</g>

			{/* Высота проёма и подоконника — прямо у него, как на чертеже; поверх мебели */}
			{showDimensions &&
				elevation.openings.map(({ opening, x0, x1, y0, y1 }) => {
					const middle = x0 + (x1 - x0) / 2;
					return (
						<g key={opening.id} style={{ pointerEvents: "none" }}>
							<DimensionLine
								from={at(middle, y0)}
								to={at(middle, y1)}
								normal={{ x: -1, y: 0 }}
								distance={0}
								mmPerPx={mmPerPx}
							/>
							{y0 > 250 && (
								<DimensionLine
									from={at(middle, 0)}
									to={at(middle, y0)}
									normal={{ x: -1, y: 0 }}
									distance={0}
									mmPerPx={mmPerPx}
								/>
							)}
						</g>
					);
				})}

			{/* Высота от пола у предметов, висящих на стене (навесной шкаф, телевизор), — как у подоконника */}
			{showDimensions &&
				elevation.floorItems.map(({ item, x, y, width: itemWidth }) =>
					y > 250 ? (
						<g key={item.id} style={{ pointerEvents: "none" }}>
							<DimensionLine
								from={at(x + itemWidth / 4, 0)}
								to={at(x + itemWidth / 4, y)}
								normal={{ x: -1, y: 0 }}
								distance={0}
								mmPerPx={mmPerPx}
							/>
						</g>
					) : null,
				)}

			{/* Точки на стене */}
			{elevation.fixtures.map(({ fixture, x, y }) => {
				const point = at(x, y);
				return (
					<g key={fixture.id}>
						{showDimensions && (
							<FixtureDimensions
								sheet={sheet}
								mmPerPx={mmPerPx}
								x={x}
								y={y}
								size={fixtureFrontSize(fixture)}
							/>
						)}
						<g transform={`translate(${point.x} ${point.y})`}>
							{fixture.ref.catalog === CatalogSectionKind.Electrical ||
							fixture.ref.catalog === CatalogSectionKind.Lighting ? (
								<ElectricalItemShape
									fixture={fixture}
									item={getCatalogItem(fixture.ref)}
									selected={
										selection?.kind === SelectionKind.Fixture && selection.id === fixture.id
									}
									mmPerPx={mmPerPx}
									interactive={interactive}
									minPx={FIXTURE_MIN_PX}
									onPointerDown={(e) => onFixturePointerDown(e, fixture.id)}
								/>
							) : isWaterPoint(getCatalogItem(fixture.ref)) ? (
								<WaterPointShape
									fixture={fixture}
									item={getCatalogItem(fixture.ref)}
									selected={
										selection?.kind === SelectionKind.Fixture && selection.id === fixture.id
									}
									mmPerPx={mmPerPx}
									interactive={interactive}
									minPx={FIXTURE_MIN_PX}
									facing="front"
									onPointerDown={(e) => onFixturePointerDown(e, fixture.id)}
								/>
							) : (
								<PlumbingItemShape
									fixture={fixture}
									item={getCatalogItem(fixture.ref)}
									selected={
										selection?.kind === SelectionKind.Fixture && selection.id === fixture.id
									}
									mmPerPx={mmPerPx}
									interactive={interactive}
									minPx={FIXTURE_MIN_PX}
									facing="front"
									onPointerDown={(e) => onFixturePointerDown(e, fixture.id)}
								/>
							)}
						</g>
					</g>
				);
			})}

			{showDimensions && (
				<>
					{/* Высота стены — по линии её контура */}
					<DimensionLine
						from={at(0, 0)}
						to={at(0, height)}
						normal={{ x: -1, y: 0 }}
						distance={0}
						mmPerPx={mmPerPx}
					/>

					{/* Цепочка проёмов снизу */}
					<Chain
						sheet={sheet}
						mmPerPx={mmPerPx}
						stops={openingStops(elevation.openings, width)}
						distance={420}
					/>
				</>
			)}

			{/* Подпись стены */}
			<text
				x={sheet.x}
				y={top.y - 220}
				fontSize={px(13)}
				fontWeight={600}
				fill="#39414f"
				style={{ userSelect: "none" }}
			>
				Стена {elevation.index} · {formatMillimeters(width, unit)}
			</text>
		</g>
	);
};

/**
 * Локальная привязка настенного прибора: до ближайшего угла и от чистого пола — от его
 * ближайших краёв, как размечают рулеткой: от угла до края розетки, от пола до её низа.
 * Прибор стоит центром в точке (`x`, `y`), `size` — его габарит спереди, мм.
 */
function FixtureDimensions({
	sheet,
	mmPerPx,
	x,
	y,
	size,
}: {
	sheet: SheetLayout;
	mmPerPx: number;
	x: number;
	y: number;
	size: { width: number; height: number };
}) {
	const { width } = sheet.elevation;
	const nearLeft = x <= width / 2;
	const wallX = nearLeft ? 0 : width;
	// Край прибора, что смотрит на ближайший угол, и его низ
	const edgeX = nearLeft ? x - size.width / 2 : x + size.width / 2;
	const bottom = Math.max(0, y - size.height / 2);
	const horizontalFrom = sheetPoint(sheet, wallX, y);
	const edge = sheetPoint(sheet, edgeX, y);
	const floorUnder = sheetPoint(sheet, x, 0);
	const bottomEdge = sheetPoint(sheet, x, bottom);

	return (
		<g>
			<DimensionLine
				from={horizontalFrom}
				to={edge}
				normal={{ x: 0, y: nearLeft ? 1 : -1 }}
				distance={140}
				mmPerPx={mmPerPx}
				minLength={1}
			/>
			<DimensionLine
				from={floorUnder}
				to={bottomEdge}
				normal={{ x: nearLeft ? 1 : -1, y: 0 }}
				distance={120}
				mmPerPx={mmPerPx}
				minLength={1}
			/>
		</g>
	);
}

/** Габарит прибора на стене спереди, мм; тип удалён из каталога — точка без размеров. */
const fixtureFrontSize = (fixture: Fixture) => {
	const item = getCatalogItem(fixture.ref);
	if (!item) return { width: 0, height: 0 };
	const size = itemDimensions(fixture, item);
	return { width: size.width, height: size.height };
};

/** Горизонтальная размерная цепочка под развёрткой. */
function Chain({
	sheet,
	mmPerPx,
	stops,
	distance,
}: {
	sheet: SheetLayout;
	mmPerPx: number;
	stops: number[];
	distance: number;
}) {
	const unique = [...new Set(stops.map((s) => Math.round(s)))].toSorted((a, b) => a - b);
	return (
		<g>
			{unique.slice(0, -1).map((from, i) => (
				<DimensionLine
					key={from}
					from={sheetPoint(sheet, from, 0)}
					to={sheetPoint(sheet, unique[i + 1], 0)}
					normal={{ x: 0, y: 1 }}
					distance={distance}
					mmPerPx={mmPerPx}
				/>
			))}
		</g>
	);
}

/** Границы цепочки проёмов: края стены и края каждого проёма. */
function openingStops(openings: { x0: number; x1: number }[], width: number): number[] {
	const stops = [0, width];
	for (const opening of openings) {
		stops.push(opening.x0, opening.x1);
	}
	return stops;
}
